/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useState, useEffect } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { IClassificationProps, ICmnInspectionProps, ToggleValue } from 'interface/inspection/InspectionInfo';
import useInspection from 'hooks/useInspection';

const options = [
  { label: '전혀 동의하지 않음', value: 'NN', color: '#ff0000' },
  { label: '동의하지 않음', value: 'N', color: '#ffa500' },
  { label: '동의함', value: 'A', color: '#00ff00' },
  { label: '전적으로 동의함', value: 'AA', color: '#0088ff' },
];

interface ISelectMenuChangeProps {
  item: IClassificationProps;
  handleMenuStatus?: (_open: boolean, _item: IClassificationProps) => void;
}
function SelectMenuChange({ item, handleMenuStatus }: ISelectMenuChangeProps): JSX.Element {
  const {
    // changeResultInspection, // 1단계에서는 사용하지 않음
    // changeResultInspection2Info, // 제거됨 - 새로 만들 예정
    // changeResultInspection3Info, // 제거됨 - 새로 만들 예정
    classification,
    updateInspectionFirstClassificationItem,
    updateInspectionSecondClassificationItem,
    updateInspectionThirdClassificationItem,
  } = useInspection();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  useEffect(() => {
    if (handleMenuStatus) {
      handleMenuStatus(open, item);
    }
  }, [classification, handleMenuStatus, item, open]);
  const handleSelect = (value: string) => {
    if (classification === '1') {
      handleClassification1(value as ToggleValue);
    } else if (['2_1', '2_2', '2_3', '2_4'].includes(classification)) {
      handleClassification2(value as ToggleValue);
    } else if (['3'].includes(classification)) {
      handleClassification3(value as ToggleValue);
    }
    handleClose();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClassification1 = async (newCheck: ToggleValue) => {
    if (!newCheck || !['NN', 'N', 'A', 'AA'].includes(newCheck)) {
      return;
    }

    const resultObj: ICmnInspectionProps = {
      ...item,
      value: newCheck,
      type: newCheck,
      parent: newCheck, // 1단계에서는 value를 parent로 사용
    };

    // 기존 카테고리와 새 카테고리
    const oldParent = item.value as 'NN' | 'N' | 'A' | 'AA' | undefined;
    const newParent = newCheck as 'NN' | 'N' | 'A' | 'AA';

    // 기존 카테고리에서 제거하고 새 카테고리에 추가
    await updateInspectionFirstClassificationItem(newParent, resultObj, oldParent);
  };
  const handleClassification2 = async (newCheck: ToggleValue) => {
    if (!newCheck || !['NN', 'N', 'A', 'AA'].includes(newCheck)) {
      return;
    }

    const parentValue = item.parent;
    const typeKey = `${parentValue}Temp${newCheck}` as keyof import('interface/inspection/InspectionInfo').IInspectionSecondClassificationProps;

    const resultObj: ICmnInspectionProps = {
      ...item,
      value: newCheck,
      type: typeKey,
      parent: parentValue || '',
    };

    // inspectionSecondClassification에 저장
    const oldType = item.type as keyof import('interface/inspection/InspectionInfo').IInspectionSecondClassificationProps | undefined;
    await updateInspectionSecondClassificationItem(typeKey, resultObj, oldType);
  };
  const getSelectionValue = (selection: string): number => {
    const mapping: Record<string, number> = {
      NN: 1,
      N: 2,
      A: 3,
      AA: 4,
    };
    return mapping[selection] ?? 0;
  };
  const handleClassification3 = async (newCheck: ToggleValue) => {
    if (!newCheck || !['NN', 'N', 'A', 'AA'].includes(newCheck)) {
      return;
    }

    const parentValue = item.parent;
    if (!parentValue) return;

    const typeKey = `${parentValue}${getSelectionValue(newCheck as string)}`;
    
    const resultObj: ICmnInspectionProps = {
      ...item,
      value: newCheck,
      type: typeKey,
      parent: parentValue,
    };

    // 기존 타입 찾기 (이미 분류된 경우)
    const oldType = item.type;

    // inspectionThirdClassification에 저장
    await updateInspectionThirdClassificationItem(typeKey, resultObj, oldType);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? 'demo-positioned-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{ color: '#8D8D8D', p: '0px 8px' }}
      >
        수정 &nbsp;&gt;
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ mt: 1 }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              bgcolor: '#ffffff',
            },
          },
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 32,
            color: '#007BFF', // 수정 버튼 색상
            '&:hover': {
              bgcolor: 'transparent', // 배경색을 투명하게 유지
              color: '#0056b3', // 수정 버튼 색상 변화
            },
            pointerEvents: 'none', // 수정 버튼은 클릭 불가
          }}
        >
          수정
        </MenuItem>
        {options
          .filter(opt => opt.value !== item.value) // 현재 선택값 제외
          .map(option => (
            <MenuItem
              key={option.value}
              onClick={() => handleSelect(option.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                height: 32,
                '&:hover': {
                  bgcolor: option.color, // 마우스 오버 시 배경색 변경
                  color: '#ffffff', // 텍스트 색상 변경
                },
              }}
            >
              {/* 왼쪽 원형 색상 아이콘 */}
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: option.color,
                  borderRadius: '50%',
                }}
              />
              {option.label}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
}

export default memo(SelectMenuChange);
